import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Check } from "lucide-react";
import { LANGS, applyHtmlDirForLang } from "../i18n";

/**
 * Language switcher pill — lives in the top-right of PageHeader.
 *
 * Click the globe to open a dropdown of the 6 supported languages. The
 * current choice is persisted in localStorage via i18next-browser-languagedetector,
 * and RTL/LTR is applied to `<html>` on every change so Arabic + Farsi flip
 * the layout direction automatically.
 *
 * Keyboard: focus the trigger and press Enter to open. Click outside closes.
 */
export default function LanguageSwitcher({ className = "" }) {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const current = LANGS.find((l) => l.code === i18n.language) || LANGS[0];

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [open]);

  const choose = (code) => {
    i18n.changeLanguage(code);
    applyHtmlDirForLang(code);
    setOpen(false);
  };

  return (
    <div ref={wrapRef} className={`relative ${className}`} data-testid="lang-switcher">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-[var(--gold)]/40 hover:border-[var(--gold)] text-[var(--ink)] hover:text-[var(--emerald)] transition-colors text-[11px] uppercase tracking-[0.18em]"
        aria-label={t("language.switcher", "Language")}
        aria-expanded={open}
        data-testid="lang-switcher-trigger"
      >
        <Globe size={12} strokeWidth={1.5} />
        <span className="font-medium">{current.short}</span>
      </button>

      {open && (
        <div
          className="absolute right-0 mt-1.5 z-[var(--z-overlay,60)] min-w-[180px] bg-[var(--ivory)] border border-[var(--gold)]/40 shadow-2xl"
          role="listbox"
          data-testid="lang-switcher-menu"
        >
          {LANGS.map((l) => {
            const active = l.code === current.code;
            return (
              <button
                key={l.code}
                type="button"
                role="option"
                aria-selected={active}
                onClick={() => choose(l.code)}
                className={`w-full px-4 py-2.5 flex items-center justify-between text-sm transition-colors ${
                  active
                    ? "bg-[var(--gold)]/10 text-[var(--emerald)]"
                    : "hover:bg-[var(--ivory-2)] text-[var(--ink)]"
                }`}
                data-testid={`lang-switcher-option-${l.code}`}
              >
                <span className="flex items-center gap-2.5">
                  <span aria-hidden="true">{l.flag}</span>
                  <span style={l.rtl ? { direction: "rtl" } : undefined}>
                    {l.label}
                  </span>
                </span>
                {active && (
                  <Check size={13} strokeWidth={1.75} className="text-[var(--gold)]" />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
